import { Badge } from "@/projects/Screener/components/ui/badge";
import { Button } from "@/common/components/ui/button";
import ScreenerService from "@/services/Screener/APIs/ScreenerService";
import {
  BonusEventsRow,
  CompanyRightsIssueEventsRow,
  CompanyAGMEventsRow,
  CompanyDividendEventsRow,
  CompanyChangeOfNameEventsRow,
} from "@/utils/types/Screener/Screener";
import { AnimatePresence, motion } from "framer-motion";
import {
  Calendar,
  ChevronDown,
  Gift,
  Scissors,
  Megaphone,
  Coins,
  FileText,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";

interface EventsTabProps {
  company: any;
}

const EventsTab = ({ company }: EventsTabProps) => {
  const [eventsFilter, setEventsFilter] = useState("all");
  const [expandedEvents, setExpandedEvents] = useState<Set<string>>(new Set());
  const [bonusData, setBonusData] = useState<BonusEventsRow[]>([]);
  const [rightsData, setRightsData] = useState<CompanyRightsIssueEventsRow[]>(
    []
  );
  const [agmData, setAgmData] = useState<CompanyAGMEventsRow[]>([]);
  const [activeDot, setActiveDot] = useState<string>("");
  const [splitData, setSplitData] = useState<BonusEventsRow[]>([]);
  const [dividendData, setDividendData] = useState<CompanyDividendEventsRow[]>(
    []
  );
  const [changeOfNameData, setChangeOfNameData] = useState<
    CompanyChangeOfNameEventsRow[]
  >([]);

  useEffect(() => {
    if (
      (eventsFilter === "all" ||
        eventsFilter === "bonus" ||
        eventsFilter === "splits") &&
      bonusData.length === 0 &&
      company?.co_code
    ) {
      ScreenerService.getBonusEventsData(company.co_code)
        .then(res => {
          if (res.success && res.data) {
            const relevant = res.data.filter(
              item => item.co_code === company.co_code
            );
            setBonusData(relevant);
          }
        })
        .catch(err => console.error("Failed to fetch bonus events:", err));

      ScreenerService.getCompanySplitEventsData(company.co_code)
        .then(res => {
          if (res.success && res.data) {
            const relevant = res.data.filter(
              item => item.co_code === company.co_code
            );
            setSplitData(relevant);
          }
        })
        .catch(err => console.error("Failed to fetch bonus events:", err));
    }

    if (
      (eventsFilter === "all" || eventsFilter === "rightsIssue") &&
      rightsData.length === 0 &&
      company?.co_code
    ) {
      ScreenerService.getCompanyRightsIssueEventsData(company.co_code)
        .then(res => {
          if (res.success && res.data) {
            const relevant = res.data.filter(
              item => item.co_code === company.co_code
            );
            setRightsData(relevant);
          }
        })
        .catch(err => console.error("Failed to fetch rights events:", err));
    }

    if (
      (eventsFilter === "all" || eventsFilter === "agm") &&
      agmData.length === 0 &&
      company?.co_code
    ) {
      ScreenerService.getCompanyAGMEventsData(company.co_code)
        .then(res => {
          if (res.success && res.data) {
            const relevant = res.data.filter(
              item => item.co_code === company.co_code
            );
            setAgmData(relevant);
          }
        })
        .catch(err => console.error("Failed to fetch AGM events:", err));
    }

    if (
      (eventsFilter === "all" || eventsFilter === "dividends") &&
      dividendData.length === 0 &&
      company?.co_code
    ) {
      ScreenerService.getCompanyDividendEventsData(company.co_code)
        .then(res => {
          if (res.success && res.data) {
            const relevant = res.data.filter(
              item => item.co_code === company.co_code
            );
            setDividendData(relevant);
          }
        })
        .catch(err => console.error("Failed to fetch dividend events:", err));
    }

    if (
      (eventsFilter === "all" || eventsFilter === "other") &&
      changeOfNameData.length === 0 &&
      company?.co_code
    ) {
      ScreenerService.getCompanyChangeOfNameEventsData(company.co_code)
        .then(res => {
          if (res.success && res.data) {
            const relevant = res.data.filter(
              item => item.co_code === company.co_code
            );
            setChangeOfNameData(relevant);
          }
        })
        .catch(err =>
          console.error("Failed to fetch change of name events:", err)
        );
    }
  }, [
    eventsFilter,
    company?.co_code,
    bonusData.length,
    rightsData.length,
    agmData.length,
    splitData.length,
    dividendData.length,
    changeOfNameData.length,
  ]);

  const CORPORATE_ACTIONS_DATA = useMemo(() => {
    const actions: any[] = [];
    const source = company?.corporateActions ?? {};

    const categories: Record<string, { icon: any }> = {
      dividends: { icon: Coins },
      agm: { icon: FileText },
      bonus: { icon: Gift },
      splits: { icon: Scissors },
      announcements: { icon: Megaphone },
      rightsIssue: { icon: FileText },
    };

    Object.entries(categories).forEach(([key, { icon }]) => {
      if (source[key]) {
        source[key].forEach((item: any, index: number) => {
          const dateStr = item.date ?? "";
          const yearMatch = dateStr.match(/\d{4}/);
          const year = yearMatch
            ? parseInt(yearMatch[0])
            : new Date().getFullYear();

          actions.push({
            id: `${key}-${index}-${dateStr}`,
            category: key,
            date: dateStr,
            year: year,
            title: item.title ?? item.details ?? "Event",
            details: item.details ?? item.title ?? "",
            description: item.description ?? "",
            link: item.link,
            icon: icon,
          });
        });
      }
    });

    // Map API bonus data
    if (bonusData.length > 0) {
      bonusData.forEach((item, index) => {
        const dateStr = item.announcement_date
          ? new Date(item.announcement_date).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })
          : "";
        const year = item.announcement_date
          ? new Date(item.announcement_date).getFullYear()
          : new Date().getFullYear();

        // Determine category and icon based on some logic if mixed in API,
        // otherwise default to Bonus (Gift) or check description/remark for 'split'
        let icon = Gift;
        let category = "bonus";
        const titleLower = (
          item.description ??
          item.remark ??
          ""
        ).toLowerCase();
        if (titleLower.includes("split")) {
          icon = Scissors;
          category = "splits";
        }

        actions.push({
          id: `bonus-api-${item.id ?? index}`,
          category: category,
          date: dateStr,
          year: year,
          title: item.bonus_ratio
            ? `Bonus Issue ${item.bonus_ratio}`
            : (item.description ?? "Bonus/Split"),
          details: item.remark ?? "",
          description: item.description ?? "",
          link: "",
          icon: icon,
        });
      });
    }

    // Map API Rights Issue data
    if (rightsData.length > 0) {
      rightsData.forEach((item, index) => {
        const dateStr = item.announcement_date
          ? new Date(item.announcement_date).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })
          : "";
        const year = item.announcement_date
          ? new Date(item.announcement_date).getFullYear()
          : new Date().getFullYear();

        actions.push({
          id: `rights-api-${item.co_code ?? index}-${dateStr}`,
          category: "rightsIssue",
          date: dateStr,
          year: year,
          title: `Rights Issue ${item.rights_ratio}`,
          details: `Premium: ${item.premium} | Face Value: ${item.face_value} | \n${item.remark}`,
          description: item.description ?? "",
          link: "", // API doesn't seem to provide a PDF link
          icon: FileText,
        });
      });
    }

    // Map API AGM data
    if (agmData.length > 0) {
      agmData.forEach((item, index) => {
        const dateStr = item.announcement_date
          ? new Date(item.announcement_date).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })
          : "";
        const year = item.announcement_date
          ? new Date(item.announcement_date).getFullYear()
          : new Date().getFullYear();

        actions.push({
          id: `agm-api-${item.co_code ?? index}-${dateStr}`,
          category: "agm",
          date: dateStr,
          year: year,
          title: `AGM`,
          details: `GM Date: ${item.gm_date} | Purpose: ${item.purpose}`,
          description: item.description ?? "",
          link: "", // API doesn't seem to provide a PDF link
          icon: FileText,
        });
      });
    }

    // Map API Split data
    if (splitData.length > 0) {
      splitData.forEach((item, index) => {
        const dateStr = item.announcement_date
          ? new Date(item.announcement_date).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })
          : "";
        const year = item.announcement_date
          ? new Date(item.announcement_date).getFullYear()
          : new Date().getFullYear();

        actions.push({
          id: `split-api-${item.id ?? index}`,
          category: "splits",
          date: dateStr,
          year: year,
          title: item.remark ?? "Stock Split",
          details: `FV Before: ${item.fv_before} | FV After: ${item.fv_after} \n${item.remark ?? ""}`,
          description: item.description ?? "",
          link: "",
          icon: Scissors,
        });
      });
    }

    // Map API Dividend data
    if (dividendData.length > 0) {
      dividendData.forEach((item, index) => {
        const dateStr = item.announcement_date
          ? new Date(item.announcement_date).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })
          : "";
        const year = item.announcement_date
          ? new Date(item.announcement_date).getFullYear()
          : new Date().getFullYear();

        actions.push({
          id: `dividend-api-${item.co_code ?? index}`,
          category: "dividends",
          date: dateStr,
          year: year,
          title: item.remark ?? "Dividend",
          details: `Amount: ${item.div_amount} | Per: ${item.div_per} \n${item.remark ?? ""}`,
          description: item.description ?? "",
          link: "",
          icon: Scissors,
        });
      });
    }

    // Map API Change of Name data
    if (changeOfNameData.length > 0) {
      changeOfNameData.forEach((item, index) => {
        const dateStr = item.effective_date
          ? new Date(item.effective_date).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })
          : "";
        const year = item.effective_date
          ? new Date(item.effective_date).getFullYear()
          : new Date().getFullYear();

        actions.push({
          id: `change-of-name-api-${item.co_code ?? index}-${dateStr}`,
          category: "other",
          date: dateStr,
          year: year,
          title: "Change of Company Name",
          details: `Old Name: ${item.old_name} | New Name: ${item.new_name}`,
          description: item.old_name + " -> " + item.new_name,
          link: "",
          icon: Scissors,
        });
      });
    }

    // Sort by year desc, then by date if possible
    return actions.sort((a, b) => b.year - a.year);
  }, [company, bonusData, rightsData, agmData, splitData, changeOfNameData]);

  const toggleEventExpand = (id: string) => {
    const newExpanded = new Set(expandedEvents);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedEvents(newExpanded);
  };

  const filteredEvents = CORPORATE_ACTIONS_DATA.filter(
    e =>
      eventsFilter === "all" ||
      e.category === eventsFilter ||
      (eventsFilter === "bonus" &&
        (e.category === "bonus" || e.category === "splits"))
  );

  const uniqueYears = Array.from(new Set(filteredEvents.map(e => e.year))).sort(
    (a, b) => b - a
  );

  const OnMouseEnter = (id: string) => {
    if (id === activeDot) {
      setActiveDot("");
    } else {
      setActiveDot(id);
    }
  };

  const OnMouseLeave = (_id: string) => {
    setActiveDot("");
  };

  return (
    <div className="mx-auto max-w-6xl space-y-8 font-sans duration-500 animate-in fade-in">
      {/* Event Category Filters */}
      <div className="flex flex-wrap items-center gap-2 pb-2">
        {[
          { id: "all", label: "All Events" },
          { id: "dividends", label: "Dividends" },
          { id: "agm", label: "AGM/EGM" },
          { id: "rightsIssue", label: "Rights" },
          { id: "bonus", label: "Bonus & Splits" },
          { id: "announcements", label: "Announcements" },
          { id: "other", label: "Change of Company Name" },
        ].map(filter => (
          <button
            key={filter.id}
            onClick={() => setEventsFilter(filter.id)}
            className={`rounded-xl border px-4 py-2 text-xs font-bold transition-all ${
              eventsFilter === filter.id
                ? "border-[#00D09C] bg-[#00D09C] text-white shadow-md shadow-[#00D09C]/20"
                : "border-gray-100 bg-white text-gray-500 hover:border-gray-300 hover:text-gray-700"
            }`}
          >
            {filter.label}
          </button>
        ))}
      </div>

      {/* Year-wise Timeline */}
      <div className="timeline-container relative">
        {/* Continuous Line (optional: if we want a single long line, we'd need absolute here, 
            but per year block is easier to manage. Let's try per-year with negative margins if needed 
            or just keep the per-card line which is cleaner) */}

        {uniqueYears.map(year => (
          <div key={year} className="relative mb-8">
            {/* Year Header */}
            <div className="mb-6 flex items-center gap-4">
              <span className="text-2xl font-bold text-gray-900">{year}</span>
              <div className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-[#00D09C] text-xs font-bold text-[#00D09C]">
                {year.toString().slice(-2)}
              </div>
            </div>

            <div className="ml-[15px] space-y-6 border-l-2 border-gray-100 pl-8">
              {filteredEvents
                .filter(e => e.year === year)
                .map(event => (
                  <div key={event.id} className="relative">
                    {/* Timeline Dot */}
                    <div
                      className={`absolute -left-[40px] top-10 h-3 h-4 w-4 rounded-full border-2 border-white ${activeDot === event.id ? "bg-[#00D09C]" : "bg-gray-200"}`}
                    />

                    <div
                      onMouseEnter={() => OnMouseEnter(event.id)}
                      onMouseLeave={() => OnMouseLeave(event.id)}
                      onClick={() => toggleEventExpand(event.id)}
                      className="group cursor-pointer rounded-xl border border-gray-100 bg-white p-5 shadow-sm transition-all hover:shadow-md"
                    >
                      <div className="flex flex-col gap-4">
                        <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-start">
                          <div className="flex items-start gap-4">
                            <div
                              className={`rounded-xl p-3 ${
                                event.category === "dividends"
                                  ? "bg-green-50 text-green-600"
                                  : event.category === "agm"
                                    ? "bg-blue-50 text-blue-600"
                                    : event.category === "announcements"
                                      ? "bg-purple-50 text-purple-600"
                                      : event.category === "bonus" ||
                                          event.category === "splits"
                                        ? "bg-orange-50 text-orange-600"
                                        : "bg-gray-50 text-gray-600"
                              }`}
                            >
                              <event.icon className="h-5 w-5" />
                            </div>
                            <div className="space-y-1.5">
                              <div className="flex items-center gap-3">
                                <span className="flex items-center gap-1.5 text-[11px] font-extrabold uppercase tracking-wide text-[#00D09C]">
                                  <Calendar className="h-3 w-3" /> {event.date}
                                </span>
                                <Badge
                                  variant="outline"
                                  className={`h-5 px-1.5 text-[10px] font-bold uppercase ${
                                    event.category === "dividends"
                                      ? "border-green-100 bg-green-50/50 text-green-600"
                                      : event.category === "agm"
                                        ? "border-blue-100 bg-blue-50/50 text-blue-600"
                                        : event.category === "announcements"
                                          ? "border-purple-100 bg-purple-50/50 text-purple-600"
                                          : "border-orange-100 bg-orange-50/50 text-orange-600"
                                  }`}
                                >
                                  {event.category.toUpperCase()}
                                </Badge>
                              </div>
                              <h4 className="text-lg font-bold text-gray-900 transition-colors group-hover:text-[#00D09C]">
                                {event.title}
                              </h4>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleEventExpand(event.id)}
                            className="h-8 text-[10px] font-bold text-[#00D09C] hover:bg-[#00D09C]/5 hover:text-[#00D09C]"
                          >
                            {expandedEvents.has(event.id)
                              ? "SHOW LESS"
                              : "READ MORE"}
                            {expandedEvents.has(event.id) ? (
                              <ChevronDown className="ml-1 h-3 w-3 rotate-180 transition-transform" />
                            ) : (
                              <ChevronDown className="ml-1 h-3 w-3 transition-transform" />
                            )}
                          </Button>
                        </div>

                        <AnimatePresence>
                          {expandedEvents.has(event.id) && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              className="overflow-hidden"
                            >
                              <div className="border-t border-gray-50 pb-1 pt-2">
                                <p className="pt-3 text-sm font-medium leading-relaxed text-gray-500">
                                  {event.details}
                                </p>
                                <p className="pt-3 text-sm font-medium leading-relaxed text-gray-500">
                                  {event.description}
                                </p>
                                <div className="mt-4 flex items-center gap-4">
                                  {event.link && (
                                    <a
                                      href={event.link}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="border-b-2 border-transparent pb-0.5 text-[10px] font-black text-[#00D09C] transition-all hover:border-[#00D09C]"
                                    >
                                      DOWNLOAD PDF
                                    </a>
                                  )}
                                  <button className="border-b-2 border-transparent pb-0.5 text-[10px] font-black text-gray-400 transition-all hover:border-gray-400">
                                    SHARE EVENT
                                  </button>
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        ))}
        {filteredEvents.length === 0 && (
          <div className="py-20 text-center">
            <div className="mb-6 inline-flex h-20 w-20 items-center justify-center rounded-full bg-gray-50">
              <Calendar className="h-10 w-10 text-gray-300" />
            </div>
            <h3 className="text-lg font-bold text-gray-900">No Events Found</h3>
            <p className="mx-auto mt-2 max-w-xs text-sm text-gray-500">
              There are no events matching your current filter in the selected
              timeframe.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventsTab;
